import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useThreatHunt, startHunt, completeHunt } from '../../hooks/useSecOps';

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

function Badge({ value, colors }: { value: string; colors: Record<string, string> }) {
  const label = value.replace(/_/g, ' ');
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${colors[value] ?? 'bg-gray-100 text-gray-600'}`}>{label}</span>;
}

export default function ThreatHuntDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, loading, error, refresh } = useThreatHunt(id ?? null);
  const [acting, setActing] = useState(false);
  const [summary, setSummary] = useState('');
  const [showComplete, setShowComplete] = useState(false);

  const hunt = data as Record<string, unknown> | null;

  async function handleStart() {
    if (!id) return;
    setActing(true);
    try {
      await startHunt(id);
      refresh();
    } catch { /* handled */ } finally {
      setActing(false);
    }
  }

  async function handleComplete() {
    if (!id) return;
    setActing(true);
    try {
      await completeHunt(id, { summary });
      setShowComplete(false);
      setSummary('');
      refresh();
    } catch { /* handled */ } finally {
      setActing(false);
    }
  }

  async function handleCancel() {
    if (!id) return;
    setActing(true);
    try {
      const { secopsApi } = await import('../../lib/secops-api');
      await secopsApi.post('/secops/threat-hunting', { action: 'cancel', id });
      refresh();
    } catch { /* handled */ } finally {
      setActing(false);
    }
  }

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-64" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error || !hunt) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700 font-medium">Failed to load threat hunt</p>
          <p className="text-red-500 text-sm mt-1">{error ?? 'Hunt not found'}</p>
          <button onClick={refresh} className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-medium">Retry</button>
        </div>
      </div>
    );
  }

  const status = (hunt.status as string) ?? 'draft';
  const steps = (hunt.steps as Array<{ order: number; title: string; completed: boolean; notes?: string }>) ?? [];
  const iocs = (hunt.iocs as Array<{ type: string; value: string; context?: string }>) ?? [];
  const findings = (hunt.findings as Array<{ title: string; description: string; severity: string }>) ?? [];
  const mitreTechniques: string[] = Array.isArray(hunt.mitre_techniques) ? hunt.mitre_techniques as string[] : [];

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <button onClick={() => navigate('/secops/threat-hunting')} className="text-sm text-gray-500 hover:text-gray-700">
        &larr; Back to Threat Hunting
      </button>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{hunt.title as string}</h1>
          <div className="flex items-center gap-3 mt-2">
            <Badge value={status} colors={STATUS_COLORS} />
            <span className="text-sm text-gray-500">Created: {hunt.created_at ? new Date(hunt.created_at as string).toLocaleDateString() : '-'}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {status === 'draft' && (
            <button onClick={handleStart} disabled={acting} className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
              {acting ? '...' : 'Start Hunt'}
            </button>
          )}
          {status === 'in_progress' && (
            <>
              <button onClick={() => setShowComplete(true)} disabled={acting} className="px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50">
                Complete
              </button>
              <button onClick={handleCancel} disabled={acting} className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50">
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Hypothesis */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-2">Hypothesis</h2>
        <p className="text-sm text-gray-600 whitespace-pre-wrap">{(hunt.hypothesis as string) || 'No hypothesis defined.'}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Steps */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Hunt Steps ({steps.length})</h2>
            {steps.length === 0 ? (
              <p className="text-sm text-gray-400">No steps defined.</p>
            ) : (
              <div className="space-y-3">
                {steps.sort((a, b) => a.order - b.order).map(step => (
                  <div key={step.order} className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                      step.completed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {step.completed ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      ) : step.order}
                    </div>
                    <div>
                      <p className={`text-sm ${step.completed ? 'text-gray-500 line-through' : 'text-gray-900 font-medium'}`}>{step.title}</p>
                      {step.notes && <p className="text-xs text-gray-400 mt-0.5">{step.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Findings */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Findings ({findings.length})</h2>
            {findings.length === 0 ? (
              <p className="text-sm text-gray-400">No findings yet.</p>
            ) : (
              <div className="space-y-4">
                {findings.map((f, i) => (
                  <div key={i} className="border-l-2 border-gray-200 pl-4">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-gray-900">{f.title}</p>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${
                        f.severity === 'critical' ? 'bg-red-100 text-red-700' :
                        f.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                        f.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                      }`}>{f.severity}</span>
                    </div>
                    <p className="text-sm text-gray-600">{f.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* IOCs */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">IOCs Discovered ({iocs.length})</h2>
            {iocs.length === 0 ? (
              <p className="text-sm text-gray-400">No IOCs discovered yet.</p>
            ) : (
              <div className="overflow-hidden">
                <table className="min-w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-2">Type</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-2">Value</th>
                      <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-2">Context</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {iocs.map((ioc, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-xs text-gray-700 font-medium uppercase">{ioc.type}</td>
                        <td className="px-4 py-2 text-xs text-gray-900 font-mono">{ioc.value}</td>
                        <td className="px-4 py-2 text-xs text-gray-500">{ioc.context || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* MITRE Coverage */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">MITRE Coverage</h2>
            {mitreTechniques.length === 0 ? (
              <p className="text-sm text-gray-400">No MITRE techniques mapped.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {mitreTechniques.map((t, i) => (
                  <span key={i} className="bg-indigo-50 text-indigo-700 text-xs px-2 py-1 rounded font-mono">{t}</span>
                ))}
              </div>
            )}
          </div>

          {/* Summary (if completed) */}
          {!!hunt.summary && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-3">Summary</h2>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{String(hunt.summary)}</p>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Details</h2>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Data Sources</p>
                <div className="flex flex-wrap gap-1">
                  {((hunt.data_sources as string[]) ?? []).map((s, i) => (
                    <span key={i} className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded">{s}</span>
                  ))}
                  {((hunt.data_sources as string[]) ?? []).length === 0 && <span className="text-gray-400 text-xs">None specified</span>}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Started</p>
                <p className="text-gray-700">{hunt.started_at ? new Date(hunt.started_at as string).toLocaleString() : 'Not started'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Completed</p>
                <p className="text-gray-700">{hunt.completed_at ? new Date(hunt.completed_at as string).toLocaleString() : '-'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Complete Modal */}
      {showComplete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowComplete(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Complete Hunt</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Summary</label>
              <textarea value={summary} onChange={e => setSummary(e.target.value)}
                rows={4} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Summarize findings and conclusions..." />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowComplete(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleComplete} disabled={acting}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50">
                {acting ? 'Completing...' : 'Complete Hunt'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
